import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {VerdocsBuilderFields, VerdocsTemplateFields} from '@verdocs/web-sdk-react';
import {IUserPreferences, sendTemplateFeedback, VerdocsEndpoint} from '@verdocs/js-sdk';
import {saveMyPreferences, useContrast, useMyUser} from '../../../react-sdk/queries';
import {SwitchBackDialog} from './SwitchBackDialog';
import {Button} from '../../../Components';

interface TemplateFieldsEditorProps {
  templateId: string;
  onTemplateUpdated: () => void;
}

export const TemplateFieldsEditor: FC<TemplateFieldsEditorProps> = ({templateId, onTemplateUpdated}) => {
  const userQuery = useMyUser();
  const contrast = useContrast();
  const [saving, setSaving] = useState(false);
  const [askingWhy, setAskingWhy] = useState(false);

  const setBuilder = (template_builder: IUserPreferences['template_builder']) => {
    setSaving(true);
    return saveMyPreferences({template_builder})
      .catch((e) => {
        console.log('Error saving builder preference', e);
        toast.error('Error switching builders. Please try again.');
      })
      .finally(() => setSaving(false));
  };

  const handleSwitchBack = async (comments: string) => {
    if (comments) {
      sendTemplateFeedback(VerdocsEndpoint.getDefault(), templateId, {
        comments,
        source: 'builder-switch-back',
        url: window.location.href,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      }).catch((e) => console.log('Error sending switch-back feedback', e));
    }

    await setBuilder('build');
    setAskingWhy(false);
  };

  if (userQuery.isPending) {
    return null;
  }

  const useBuilder = userQuery.data?.preferences?.template_builder === 'builder';

  return (
    <>
      <div className="flex flex-none flex-row items-center gap-4 px-5 py-2 bg-white border-b border-[#e3e5ee] text-[13px] text-[#33364b]">
        {useBuilder ? (
          <>
            <div className="flex-1">You're using the new Builder.</div>
            <Button
              size="xsmall"
              variant="secondary"
              label="Switch back"
              className="uppercase tracking-wide"
              style={{fontSize: 11}}
              onClick={() => setAskingWhy(true)}
              disabled={saving}
            />
          </>
        ) : (
          <>
            <div className="flex-1">Our new Builder lets you build better, smarter forms more quickly.</div>
            <Button
              size="xsmall"
              label="Try it now"
              className="uppercase tracking-wide"
              style={{fontSize: 11}}
              onClick={() => setBuilder('builder')}
              disabled={saving}
            />
          </>
        )}
      </div>

      {useBuilder ? (
        // The Builder sizes its side panels to this container.
        <div className="flex-1 min-h-0 overflow-y-auto [container-type:size]">
          <VerdocsBuilderFields templateId={templateId} contrast={contrast} showTour onTemplateUpdated={onTemplateUpdated} />
        </div>
      ) : (
        <>
          <div id="verdocs-fields-toolbar" />
          <VerdocsTemplateFields
            templateId={templateId}
            toolbarTargetId="verdocs-fields-toolbar"
            onTemplateUpdated={onTemplateUpdated}
            className="flex flex-1 flex-col items-center justify-center overflow-y-scroll border-box"
          />
        </>
      )}

      {askingWhy && <SwitchBackDialog onCancel={() => setAskingWhy(false)} onSwitchBack={handleSwitchBack} />}
    </>
  );
};
